import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Activity,
  BarChart3,
  CheckCircle2,
  Clipboard,
  Code2,
  Copy,
  ExternalLink,
  KeyRound,
  Loader2,
  Newspaper,
  Play,
  Search,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { apiClient } from '@/lib/api'
import { cn, copyToClipboard } from '@/lib/utils'
import { useToast } from '@/components/ui/use-toast'
import type { ApiKey } from '@/types'
import { AgentSetupCard } from '@/components/agent-setup-card'

type ParamType = 'string' | 'number' | 'integer' | 'select'

type EndpointParam = {
  name: string
  label: string
  in: 'path' | 'query'
  type: ParamType
  required?: boolean
  defaultValue?: string
  options?: string[]
  placeholder?: string
}

type Endpoint = {
  id: string
  category: string
  method: 'GET'
  path: string
  title: string
  description: string
  credits: number
  params: EndpointParam[]
}

type ResponseState = {
  status: number
  statusText: string
  elapsedMs: number
  creditCost: number | null
  creditsRemaining: number | null
  unkeyKeyId: string | null
  body: unknown
}

type RequestHistoryItem = {
  id: number
  endpointTitle: string
  method: string
  path: string
  status: number
  elapsedMs: number
  creditCost: number | null
  creditsRemaining: number | null
}

type FreeCreditRefillStatus = {
  canRefill: boolean
  alreadyClaimed: boolean
  period: string
  nextRefillAt: string
  credits: number
  claimedAt: string | null
}

const PUBLIC_API_BASE_URL = 'https://mcp.plusefin.com/api'

const endpoints: Endpoint[] = [
  {
    id: 'ticker',
    category: 'Stock Data',
    method: 'GET',
    path: '/tools/ticker/{symbol}',
    title: 'Company Overview',
    description: 'Company profile, market data, and core metrics.',
    credits: 0,
    params: [
      { name: 'symbol', label: 'Symbol', in: 'path', type: 'string', required: true, defaultValue: 'AAPL' },
    ],
  },
  {
    id: 'snapshot',
    category: 'Stock Data',
    method: 'GET',
    path: '/snapshot',
    title: 'Point-in-Time Snapshot',
    description: 'MCP tool get_point_in_time_snapshot: AI-friendly Markdown financial snapshot for a ticker as of a date.',
    credits: 1,
    params: [
      { name: 'ticker', label: 'Ticker', in: 'query', type: 'string', required: true, defaultValue: 'AAPL' },
      { name: 'as_of', label: 'As of', in: 'query', type: 'string', required: true, defaultValue: '2022-03-01', placeholder: 'YYYY-MM-DD' },
    ],
  },
  {
    id: 'price-history',
    category: 'Stock Data',
    method: 'GET',
    path: '/tools/price-history',
    title: 'Price History',
    description: 'Historical price data for a ticker.',
    credits: 1,
    params: [
      { name: 'ticker', label: 'Ticker', in: 'query', type: 'string', required: true, defaultValue: 'NVDA' },
      {
        name: 'period',
        label: 'Period',
        in: 'query',
        type: 'select',
        defaultValue: '6mo',
        options: ['1d', '5d', '1mo', '3mo', '6mo', '1y', '2y', '5y', '10y', 'ytd', 'max'],
      },
      { name: 'start_date', label: 'Start date', in: 'query', type: 'string', placeholder: 'YYYY-MM-DD' },
      { name: 'end_date', label: 'End date', in: 'query', type: 'string', placeholder: 'YYYY-MM-DD' },
    ],
  },
  {
    id: 'options',
    category: 'Options',
    method: 'GET',
    path: '/tools/options/{symbol}',
    title: 'Options Chain',
    description: 'Options chain data with filters for strike, date, and type.',
    credits: 1,
    params: [
      { name: 'symbol', label: 'Symbol', in: 'path', type: 'string', required: true, defaultValue: 'TSLA' },
      { name: 'num_options', label: 'Number of options', in: 'query', type: 'integer', defaultValue: '10' },
      { name: 'option_type', label: 'Option type', in: 'query', type: 'select', options: ['', 'C', 'P'], defaultValue: '' },
      { name: 'strike_lower', label: 'Strike lower', in: 'query', type: 'number' },
      { name: 'strike_upper', label: 'Strike upper', in: 'query', type: 'number' },
      { name: 'start_date', label: 'Start date', in: 'query', type: 'string', placeholder: 'YYYY-MM-DD' },
      { name: 'end_date', label: 'End date', in: 'query', type: 'string', placeholder: 'YYYY-MM-DD' },
    ],
  },
  {
    id: 'statements',
    category: 'Stock Data',
    method: 'GET',
    path: '/tools/statements/{symbol}',
    title: 'Financial Statements',
    description: 'Income, balance sheet, or cash flow statements.',
    credits: 1,
    params: [
      { name: 'symbol', label: 'Symbol', in: 'path', type: 'string', required: true, defaultValue: 'AAPL' },
      { name: 'statement_type', label: 'Statement type', in: 'query', type: 'select', defaultValue: 'income', options: ['income', 'balance', 'cash'] },
      { name: 'frequency', label: 'Frequency', in: 'query', type: 'select', defaultValue: 'quarterly', options: ['quarterly', 'annual'] },
    ],
  },
  {
    id: 'holders',
    category: 'Holdings & ETFs',
    method: 'GET',
    path: '/tools/holders/{symbol}',
    title: 'Institutional Holders',
    description: 'Institutional holder data for a symbol.',
    credits: 1,
    params: [
      { name: 'symbol', label: 'Symbol', in: 'path', type: 'string', required: true, defaultValue: 'AAPL' },
      { name: 'top_n', label: 'Top N', in: 'query', type: 'integer', defaultValue: '20' },
    ],
  },
  {
    id: 'earnings',
    category: 'Stock Data',
    method: 'GET',
    path: '/tools/earnings/{symbol}',
    title: 'Earnings History',
    description: 'Historical earnings data for a stock.',
    credits: 1,
    params: [
      { name: 'symbol', label: 'Symbol', in: 'path', type: 'string', required: true, defaultValue: 'AAPL' },
    ],
  },
  {
    id: 'news',
    category: 'News',
    method: 'GET',
    path: '/tools/news/{symbol}',
    title: 'Ticker News',
    description: 'Latest news for a ticker.',
    credits: 1,
    params: [
      { name: 'symbol', label: 'Symbol', in: 'path', type: 'string', required: true, defaultValue: 'NVDA' },
    ],
  },
  {
    id: 'top25',
    category: 'Holdings & ETFs',
    method: 'GET',
    path: '/tools/top25/{symbol}',
    title: 'Top 25 Holders',
    description: 'Top 25 institutional holders for a symbol.',
    credits: 1,
    params: [
      { name: 'symbol', label: 'Symbol', in: 'path', type: 'string', required: true, defaultValue: 'AAPL' },
    ],
  },
  {
    id: 'insiders',
    category: 'Stock Data',
    method: 'GET',
    path: '/tools/insiders/{symbol}',
    title: 'Insider Trading',
    description: 'Insider trading data for a symbol.',
    credits: 1,
    params: [
      { name: 'symbol', label: 'Symbol', in: 'path', type: 'string', required: true, defaultValue: 'AAPL' },
    ],
  },
  {
    id: 'options-analyze',
    category: 'Options',
    method: 'GET',
    path: '/tools/options/analyze/{symbol}',
    title: 'Options Analysis',
    description: 'Options analysis and derived signals.',
    credits: 1,
    params: [
      { name: 'symbol', label: 'Symbol', in: 'path', type: 'string', required: true, defaultValue: 'TSLA' },
    ],
  },
  {
    id: 'prediction',
    category: 'Predictions',
    method: 'GET',
    path: '/tools/prediction/{symbol}',
    title: 'Price Prediction',
    description: 'AI-assisted price prediction for a ticker.',
    credits: 2,
    params: [
      { name: 'symbol', label: 'Symbol', in: 'path', type: 'string', required: true, defaultValue: 'AAPL' },
    ],
  },
  {
    id: 'sentiment',
    category: 'Market Sentiment',
    method: 'GET',
    path: '/tools/sentiment',
    title: 'Market Sentiment',
    description: 'Current market sentiment index.',
    credits: 1,
    params: [],
  },
  {
    id: 'sentiment-history',
    category: 'Market Sentiment',
    method: 'GET',
    path: '/tools/sentiment/history',
    title: 'Sentiment History',
    description: 'Historical market sentiment values.',
    credits: 1,
    params: [
      { name: 'days', label: 'Days', in: 'query', type: 'integer', defaultValue: '10' },
    ],
  },
  {
    id: 'sentiment-trend',
    category: 'Market Sentiment',
    method: 'GET',
    path: '/tools/sentiment/trend',
    title: 'Sentiment Trend',
    description: 'Analyze recent market sentiment trend.',
    credits: 1,
    params: [
      { name: 'days', label: 'Days', in: 'query', type: 'integer', defaultValue: '10' },
    ],
  },
  {
    id: 'market-news',
    category: 'News',
    method: 'GET',
    path: '/tools/news/market',
    title: 'Market News',
    description: 'CNBC market news feed.',
    credits: 1,
    params: [],
  },
  {
    id: 'social-news',
    category: 'News',
    method: 'GET',
    path: '/tools/news/social',
    title: 'Social Discussions',
    description: 'Social media discussions filtered by keywords.',
    credits: 1,
    params: [
      { name: 'keywords', label: 'Keywords', in: 'query', type: 'string', defaultValue: 'tsla,nvda' },
    ],
  },
  {
    id: 'fred',
    category: 'Macro Data',
    method: 'GET',
    path: '/tools/fred/{series_id}',
    title: 'FRED Series',
    description: 'FRED economic series data.',
    credits: 1,
    params: [
      { name: 'series_id', label: 'Series ID', in: 'path', type: 'string', required: true, defaultValue: 'GDP' },
    ],
  },
  {
    id: 'fred-search',
    category: 'Macro Data',
    method: 'GET',
    path: '/tools/fred/search',
    title: 'Search FRED Series',
    description: 'Search FRED economic data series.',
    credits: 1,
    params: [
      { name: 'q', label: 'Search query', in: 'query', type: 'string', required: true, defaultValue: 'gdp' },
    ],
  },
]

const categoryIcons: Record<string, typeof TrendingUp> = {
  'Stock Data': TrendingUp,
  Options: BarChart3,
  'Holdings & ETFs': ShieldCheck,
  Predictions: Sparkles,
  'Market Sentiment': Activity,
  'Macro Data': Search,
  News: Newspaper,
}

const getInitialValues = (endpoint: Endpoint) => {
  return endpoint.params.reduce<Record<string, string>>((values, param) => {
    values[param.name] = param.defaultValue ?? ''
    return values
  }, {})
}

const getApiKeyValue = (apiKey?: ApiKey | null) => {
  if (!apiKey) return ''
  return apiKey.unkey || apiKey.key || apiKey.key_id || ''
}

const getApiKeyLabel = (apiKey: ApiKey) => {
  return apiKey.name || apiKey.unkey_id || apiKey.id
}

const getApiKeyRemainingCredits = (apiKey?: ApiKey) => {
  return apiKey?.remaining ?? apiKey?.credits?.remaining ?? null
}

const buildPath = (endpoint: Endpoint, values: Record<string, string>) => {
  let path = endpoint.path
  endpoint.params
    .filter((param) => param.in === 'path')
    .forEach((param) => {
      path = path.replace(`{${param.name}}`, encodeURIComponent(values[param.name] || ''))
    })

  const searchParams = new URLSearchParams()
  endpoint.params
    .filter((param) => param.in === 'query')
    .forEach((param) => {
      const value = values[param.name]
      if (value !== undefined && value !== '') {
        searchParams.set(param.name, value)
      }
    })

  const query = searchParams.toString()
  return query ? `${path}?${query}` : path
}

const formatJson = (value: unknown) => {
  if (value === undefined || value === null || value === '') return ''
  if (typeof value === 'string') return value
  return JSON.stringify(value, null, 2)
}

const getResponseData = (body: unknown) => {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data?: unknown }).data
  }

  if (typeof body === 'string') {
    return body
  }

  return null
}

const getMarkdownContent = (body: unknown) => {
  const data = getResponseData(body)

  if (data === undefined || data === null || data === '') return ''
  if (typeof data === 'string') return data

  return ''
}

const hasMarkdownResponse = (body: unknown) => getMarkdownContent(body).length > 0

const splitTableRow = (line: string) => {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())
}

const isTableSeparator = (line: string) => /^\s*\|?[\s:-]+\|[\s|:-]+\|?\s*$/.test(line)

const isMarkdownBlockStart = (line: string, nextLine?: string) => {
  const trimmed = line.trim()

  return (
    trimmed.startsWith('```') ||
    /^#{1,4}\s+/.test(trimmed) ||
    /^>\s?/.test(trimmed) ||
    /^[-*]\s+/.test(trimmed) ||
    /^\d+\.\s+/.test(trimmed) ||
    (/\|/.test(line) && Boolean(nextLine && isTableSeparator(nextLine)))
  )
}

const renderInlineMarkdown = (text: string, keyPrefix: string): ReactNode[] => {
  const nodes: ReactNode[] = []
  const pattern = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }

    const token = match[0]
    const key = `${keyPrefix}-${match.index}`

    if (token.startsWith('`')) {
      nodes.push(
        <code key={key} className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[0.85em] text-stone-800">
          {token.slice(1, -1)}
        </code>
      )
    } else if (token.startsWith('**')) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>)
    } else {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
      if (linkMatch) {
        nodes.push(
          <a key={key} href={linkMatch[2]} target="_blank" rel="noreferrer" className="font-medium text-stone-900 underline underline-offset-2">
            {linkMatch[1]}
          </a>
        )
      }
    }

    lastIndex = match.index + token.length
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }

  return nodes
}

const MarkdownPreview = ({ content }: { content: string }) => {
  if (!content) {
    return (
      <div className="min-h-[220px] rounded-2xl border border-dashed border-stone-200 bg-stone-50 p-5 text-sm text-stone-500">
        Send a request to render the response data as Markdown.
      </div>
    )
  }

  const lines = content.split(/\r?\n/)
  const elements: ReactNode[] = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]
    const trimmed = line.trim()
    const key = `md-${index}`

    if (!trimmed) {
      index += 1
      continue
    }

    if (trimmed.startsWith('```')) {
      const codeLines: string[] = []
      index += 1

      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        codeLines.push(lines[index])
        index += 1
      }

      elements.push(
        <pre key={key} className="overflow-auto rounded-2xl bg-stone-950 p-4 text-xs leading-6 text-stone-100">
          {codeLines.join('\n')}
        </pre>
      )
      index += 1
      continue
    }

    if (/\|/.test(line) && lines[index + 1] && isTableSeparator(lines[index + 1])) {
      const headers = splitTableRow(line)
      const rows: string[][] = []
      index += 2

      while (index < lines.length && /\|/.test(lines[index].trim())) {
        rows.push(splitTableRow(lines[index]))
        index += 1
      }

      elements.push(
        <div key={key} className="overflow-auto rounded-2xl border border-stone-200">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-stone-100 text-left text-stone-700">
              <tr>
                {headers.map((header, headerIndex) => (
                  <th key={`${key}-head-${headerIndex}`} className="border-b border-stone-200 px-3 py-2 font-semibold">
                    {renderInlineMarkdown(header, `${key}-head-${headerIndex}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`${key}-row-${rowIndex}`} className="odd:bg-white even:bg-stone-50">
                  {headers.map((_, cellIndex) => (
                    <td key={`${key}-cell-${rowIndex}-${cellIndex}`} className="border-b border-stone-100 px-3 py-2 align-top text-stone-700">
                      {renderInlineMarkdown(row[cellIndex] || '', `${key}-cell-${rowIndex}-${cellIndex}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
      continue
    }

    const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/)
    if (headingMatch) {
      const level = headingMatch[1].length
      const className = level === 1 ? 'text-2xl font-bold' : level === 2 ? 'text-xl font-bold' : 'text-lg font-semibold'
      const children = renderInlineMarkdown(headingMatch[2], key)

      elements.push(level === 1 ? <h1 key={key} className={className}>{children}</h1> : level === 2 ? <h2 key={key} className={className}>{children}</h2> : <h3 key={key} className={className}>{children}</h3>)
      index += 1
      continue
    }

    if (/^>\s?/.test(trimmed)) {
      elements.push(
        <blockquote key={key} className="border-l-4 border-stone-300 pl-4 text-stone-600">
          {renderInlineMarkdown(trimmed.replace(/^>\s?/, ''), key)}
        </blockquote>
      )
      index += 1
      continue
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = []

      while (index < lines.length && /^[-*]\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^[-*]\s+/, ''))
        index += 1
      }

      elements.push(
        <ul key={key} className="list-disc space-y-1 pl-5 text-stone-700">
          {items.map((item, itemIndex) => (
            <li key={`${key}-item-${itemIndex}`}>{renderInlineMarkdown(item, `${key}-item-${itemIndex}`)}</li>
          ))}
        </ul>
      )
      continue
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = []

      while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\d+\.\s+/, ''))
        index += 1
      }

      elements.push(
        <ol key={key} className="list-decimal space-y-1 pl-5 text-stone-700">
          {items.map((item, itemIndex) => (
            <li key={`${key}-item-${itemIndex}`}>{renderInlineMarkdown(item, `${key}-item-${itemIndex}`)}</li>
          ))}
        </ol>
      )
      continue
    }

    const paragraphLines = [trimmed]
    index += 1

    while (index < lines.length && lines[index].trim() && !isMarkdownBlockStart(lines[index], lines[index + 1])) {
      paragraphLines.push(lines[index].trim())
      index += 1
    }

    elements.push(
      <p key={key} className="leading-7 text-stone-700">
        {renderInlineMarkdown(paragraphLines.join(' '), key)}
      </p>
    )
  }

  return <div className="min-h-[220px] space-y-4 rounded-2xl bg-white p-5 text-sm text-stone-800">{elements}</div>
}

export default function PlaygroundApp() {
  const { toast } = useToast()
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [selectedKeyId, setSelectedKeyId] = useState('')
  const [selectedEndpointId, setSelectedEndpointId] = useState(endpoints[0].id)
  const [paramValues, setParamValues] = useState(() => getInitialValues(endpoints[0]))
  const [response, setResponse] = useState<ResponseState | null>(null)
  const [error, setError] = useState('')
  const [loadingKeys, setLoadingKeys] = useState(true)
  const [sending, setSending] = useState(false)
  const [language, setLanguage] = useState<'curl' | 'javascript' | 'python'>('curl')
  const [requestHistory, setRequestHistory] = useState<RequestHistoryItem[]>([])
  const [refreshingCredits, setRefreshingCredits] = useState(false)
  const [creditMessage, setCreditMessage] = useState('')
  const [displayedCreditsRemaining, setDisplayedCreditsRemaining] = useState<number | null>(null)
  const [responseTab, setResponseTab] = useState<'markdown' | 'json'>('json')
  const [freeCreditStatus, setFreeCreditStatus] = useState<FreeCreditRefillStatus | null>(null)

  const selectedEndpoint = endpoints.find((endpoint) => endpoint.id === selectedEndpointId) || endpoints[0]
  const selectedApiKey = apiKeys.find((apiKey) => apiKey.id === selectedKeyId) || apiKeys[0]
  const selectedApiKeyValue = getApiKeyValue(selectedApiKey)
  const selectedApiKeyRemainingCredits = getApiKeyRemainingCredits(selectedApiKey)
  const requestPath = buildPath(selectedEndpoint, paramValues)
  const publicUrl = `${PUBLIC_API_BASE_URL}${requestPath}`
  const markdownContent = response ? getMarkdownContent(response.body) : ''
  const categories = useMemo(() => {
    return Array.from(new Set(endpoints.map((endpoint) => endpoint.category)))
  }, [])

  const codeSample = useMemo(() => {
    const apiKey = selectedApiKeyValue || 'YOUR_API_KEY'

    if (language === 'javascript') {
      return `const response = await fetch('${publicUrl}', {\n  headers: {\n    Authorization: 'Bearer ${apiKey}'\n  }\n})\n\nconst data = await response.json()\nconsole.log(data)`
    }

    if (language === 'python') {
      return `import requests\n\nresponse = requests.get(\n    '${publicUrl}',\n    headers={'Authorization': 'Bearer ${apiKey}'},\n)\n\nprint(response.json())`
    }

    return `curl -s -H "Authorization: Bearer ${apiKey}" \\\n  "${publicUrl}"`
  }, [language, publicUrl, selectedApiKeyValue])

  const loadApiKeys = async (selectFirst = false) => {
    const result = await apiClient.getApiKeys()
    const keys = result.apiKeys || []
    setApiKeys(keys)

    if (selectFirst) {
      setSelectedKeyId(keys[0]?.id || '')
    }

    return keys
  }

  const formatRefillDate = (value?: string) => {
    if (!value) return 'next month'

    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(value))
  }

  useEffect(() => {
    const loadInitialApiKeys = async () => {
      try {
        await loadApiKeys(true)
      } catch (loadError) {
        console.error('Failed to load API keys:', loadError)
        setError('Failed to load API keys. Create an API key before running requests.')
      } finally {
        setLoadingKeys(false)
      }
    }

    loadInitialApiKeys()
  }, [])

  useEffect(() => {
    const loadFreeCreditStatus = async () => {
      try {
        const status = await apiClient.getFreeCreditRefillStatus()
        setFreeCreditStatus(status)
      } catch (statusError) {
        console.error('Failed to load free credit refill status:', statusError)
      }
    }

    loadFreeCreditStatus()
  }, [])

  useEffect(() => {
    setDisplayedCreditsRemaining(selectedApiKeyRemainingCredits)
  }, [selectedApiKey?.id, selectedApiKeyRemainingCredits])

  const selectEndpoint = (endpoint: Endpoint) => {
    setSelectedEndpointId(endpoint.id)
    setParamValues(getInitialValues(endpoint))
    setResponse(null)
    setError('')
    setResponseTab('json')
  }

  const updateParam = (name: string, value: string) => {
    setParamValues((current) => ({ ...current, [name]: value }))
  }

  const copyText = async (text: string) => {
    try {
      await copyToClipboard(text)
      toast({ title: 'Copied', description: 'Copied to clipboard.' })
    } catch (copyError) {
      toast({ title: 'Copy failed', description: 'Please copy it manually.', variant: 'destructive' })
    }
  }

  const sendRequest = async () => {
    setError('')
    setResponse(null)

    const missingParam = selectedEndpoint.params.find((param) => param.required && !paramValues[param.name])
    if (missingParam) {
      setError(`${missingParam.label} is required.`)
      return
    }

    if (!selectedApiKeyValue) {
      setError('Select an API key before sending a request.')
      return
    }

    try {
      setSending(true)
      const startedAt = performance.now()
      const apiResponse = await fetch(publicUrl, {
        method: selectedEndpoint.method,
        headers: {
          Authorization: `Bearer ${selectedApiKeyValue}`,
          Accept: 'application/json',
        },
      })
      const elapsedMs = Math.round(performance.now() - startedAt)
      const contentType = apiResponse.headers.get('content-type') || ''
      const creditCostHeader = apiResponse.headers.get('x-pluse-credit-cost')
      const creditsRemainingHeader = apiResponse.headers.get('x-pluse-credits-remaining')
      const unkeyKeyId = apiResponse.headers.get('x-pluse-unkey-key-id')
      const creditCost = creditCostHeader ? Number(creditCostHeader) : selectedEndpoint.credits
      const creditsRemaining = creditsRemainingHeader ? Number(creditsRemainingHeader) : null
      const nextCreditsRemaining = creditsRemaining ?? (displayedCreditsRemaining !== null && apiResponse.ok
        ? Math.max(displayedCreditsRemaining - selectedEndpoint.credits, 0)
        : displayedCreditsRemaining)
      const body = contentType.includes('application/json')
        ? await apiResponse.json()
        : await apiResponse.text()

      const responseState = {
        status: apiResponse.status,
        statusText: apiResponse.statusText,
        elapsedMs,
        creditCost,
        creditsRemaining: nextCreditsRemaining,
        unkeyKeyId,
        body,
      }

      setResponse(responseState)
      setResponseTab(hasMarkdownResponse(body) ? 'markdown' : 'json')
      setDisplayedCreditsRemaining(nextCreditsRemaining)
      setRequestHistory((items) => [
        {
          id: Date.now(),
          endpointTitle: selectedEndpoint.title,
          method: selectedEndpoint.method,
          path: requestPath,
          status: apiResponse.status,
          elapsedMs,
          creditCost,
          creditsRemaining: nextCreditsRemaining,
        },
        ...items,
      ].slice(0, 8))

      if (apiResponse.ok) {
        try {
          const refreshedKeys = await loadApiKeys()
          const refreshedSelectedKey = refreshedKeys.find((key) => key.id === selectedKeyId)
          const refreshedCreditsRemaining = getApiKeyRemainingCredits(refreshedSelectedKey)

          if (refreshedCreditsRemaining !== null) {
            setDisplayedCreditsRemaining(refreshedCreditsRemaining)
            setResponse((current) => current
              ? { ...current, creditsRemaining: refreshedCreditsRemaining }
              : current
            )
          }
        } catch (refreshError) {
          console.error('Failed to refresh API key credits:', refreshError)
        }
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Request failed')
    } finally {
      setSending(false)
    }
  }

  const refreshFreeCredits = async () => {
    try {
      setRefreshingCredits(true)
      setCreditMessage('')
      setError('')
      const result = await apiClient.refillFreeCredits()
      setCreditMessage(result.message || 'Free credits refilled.')
      setFreeCreditStatus({
        canRefill: result.canRefill,
        alreadyClaimed: result.alreadyClaimed,
        period: result.period,
        nextRefillAt: result.nextRefillAt,
        credits: result.credits,
        claimedAt: new Date().toISOString(),
      })

      if (!result.alreadyClaimed) {
        setDisplayedCreditsRemaining(result.credits)
        await loadApiKeys()
      }
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : 'Failed to refresh free credits')
    } finally {
      setRefreshingCredits(false)
    }
  }

  return (
    <div className="dashboard-page text-stone-950">
      <div className="space-y-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 shadow-sm ring-1 ring-stone-200/70">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              OpenAPI powered
            </div>
            <div>
              <h1 className="dashboard-page-title md:text-5xl">API Playground</h1>
              <p className="dashboard-page-description max-w-2xl md:text-lg">
                Explore PlusE Finance HTTP endpoints, run authenticated requests, and copy production-ready code.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="rounded-full border-stone-200 bg-white/70 text-stone-700 shadow-sm hover:bg-white hover:text-stone-950"
              asChild
            >
              <a href="https://plusefin.com/docs/api/" target="_blank" rel="noreferrer">
                REST Docs
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-stone-200 bg-white/70 text-stone-700 shadow-sm hover:bg-white hover:text-stone-950"
              asChild
            >
              <a href="https://mcp.plusefin.com/api/docs" target="_blank" rel="noreferrer">
                Swagger
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>

        <AgentSetupCard apiKey={selectedApiKeyValue} />

        <div className="grid gap-5 xl:grid-cols-[280px_minmax(0,1fr)_380px]">
          <Card className="self-start overflow-hidden bg-white/80 shadow-sm backdrop-blur-xl xl:sticky xl:top-20">
            <CardHeader className="border-b border-stone-100 pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Search className="h-5 w-5 text-stone-500" />
                Endpoint Catalog
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[calc(100vh-8rem)] space-y-5 overflow-y-auto p-4">
              {categories.map((category) => {
                const Icon = categoryIcons[category] || Code2
                const categoryEndpoints = endpoints.filter((endpoint) => endpoint.category === category)

                return (
                  <div key={category} className="space-y-2">
                    <div className="flex items-center gap-2 px-2 text-xs font-semibold uppercase tracking-[0.16em] text-stone-400">
                      <Icon className="h-3.5 w-3.5" />
                      {category}
                    </div>
                    <div className="space-y-2">
                      {categoryEndpoints.map((endpoint) => {
                        const isActive = endpoint.id === selectedEndpoint.id
                        return (
                          <button
                            key={endpoint.id}
                            type="button"
                            onClick={() => selectEndpoint(endpoint)}
                            className={cn(
                              'w-full rounded-2xl border p-3 text-left transition-all',
                              isActive
                                ? 'border-[#1f2937] bg-[#1f2937] text-white shadow-md shadow-stone-950/10'
                                : 'border-stone-200/80 bg-[#fffdfa] text-stone-700 hover:border-stone-300 hover:bg-white hover:shadow-sm'
                            )}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-sm font-semibold">{endpoint.title}</span>
                              <Badge className={cn('rounded-full text-[11px]', isActive ? 'bg-white/15 text-white' : 'bg-emerald-50 text-emerald-700')}>
                                  {endpoint.credits === 0 ? 'Free' : `${endpoint.credits} cr`}
                              </Badge>
                            </div>
                            <div className={cn('mt-2 font-mono text-[11px]', isActive ? 'text-stone-300' : 'text-stone-400')}>
                              {endpoint.method} {endpoint.path}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </CardContent>
          </Card>

          <div className="space-y-5">
            <Card className="overflow-hidden bg-white/80 shadow-sm backdrop-blur-xl">
              <CardHeader className="border-b border-stone-100">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className="rounded-full bg-[#1f2937] text-white">{selectedEndpoint.method}</Badge>
                      <Badge className="rounded-full bg-emerald-50 text-emerald-700">
                        {selectedEndpoint.credits === 0 ? 'Free' : `${selectedEndpoint.credits} credit${selectedEndpoint.credits === 1 ? '' : 's'}`}
                      </Badge>
                    </div>
                    <CardTitle className="mt-3 text-2xl">{selectedEndpoint.title}</CardTitle>
                    <p className="mt-2 text-sm text-stone-600">{selectedEndpoint.description}</p>
                  </div>
                  <Button
                    onClick={sendRequest}
                    disabled={sending || loadingKeys || !selectedApiKeyValue}
                    className="bg-[#1f2937] px-6 text-white shadow-sm hover:bg-stone-700"
                  >
                    {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                    Send request
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 p-5">
                <div className="rounded-2xl bg-[#1f2937] p-4 text-white shadow-inner shadow-black/10">
                  <div className="mb-2 flex items-center justify-between gap-3 text-xs uppercase tracking-[0.14em] text-stone-400">
                    <span>Request URL</span>
                    <button type="button" onClick={() => copyText(publicUrl)} className="inline-flex items-center gap-1 text-stone-300 hover:text-white">
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </button>
                  </div>
                  <div className="break-all font-mono text-sm text-stone-100">{publicUrl}</div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2 md:col-span-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-stone-700">
                      <KeyRound className="h-4 w-4 text-stone-400" />
                      API key
                    </label>
                    <select
                      value={selectedKeyId}
                      onChange={(event) => setSelectedKeyId(event.target.value)}
                      disabled={loadingKeys || apiKeys.length === 0}
                      className="h-11 w-full rounded-xl border border-stone-200 bg-[#fffdfa] px-3 text-sm text-stone-800 outline-none transition focus:border-stone-500 disabled:bg-stone-100"
                    >
                      {apiKeys.length === 0 ? (
                        <option value="">No API keys found</option>
                      ) : (
                        apiKeys.map((apiKey) => (
                          <option key={apiKey.id} value={apiKey.id}>{getApiKeyLabel(apiKey)}</option>
                        ))
                      )}
                    </select>
                  </div>

                  {selectedEndpoint.params.map((param) => (
                    <div key={param.name} className="space-y-2">
                      <label className="text-sm font-medium text-stone-700">
                        {param.label}
                        {param.required && <span className="text-red-500"> *</span>}
                      </label>
                      {param.type === 'select' ? (
                        <select
                          value={paramValues[param.name] || ''}
                          onChange={(event) => updateParam(param.name, event.target.value)}
                          className="h-10 w-full rounded-xl border border-stone-200 bg-[#fffdfa] px-3 text-sm outline-none transition focus:border-stone-500"
                        >
                          {(param.options || []).map((option) => (
                            <option key={option || 'empty'} value={option}>{option || 'Any'}</option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          value={paramValues[param.name] || ''}
                          onChange={(event) => updateParam(param.name, event.target.value)}
                          placeholder={param.placeholder || param.defaultValue || param.name}
                          type={param.type === 'number' || param.type === 'integer' ? 'number' : 'text'}
                          className="rounded-xl border-stone-200 bg-[#fffdfa] focus-visible:ring-stone-400"
                        />
                      )}
                      <div className="text-xs uppercase tracking-[0.12em] text-stone-400">{param.in}</div>
                    </div>
                  ))}
                </div>

                {error && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="overflow-hidden bg-white/80 shadow-sm backdrop-blur-xl">
              <CardHeader className="border-b border-stone-100 pb-4">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-lg">Response</CardTitle>
                  {response && (
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <Badge className={cn('rounded-full', response.status < 400 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>
                        {response.status} {response.statusText}
                      </Badge>
                      <Badge className="rounded-full bg-stone-100 text-stone-600">{response.elapsedMs} ms</Badge>
                      {response.creditCost !== null && (
                        <Badge className="rounded-full bg-amber-50 text-amber-700">
                          {response.creditCost} credit{response.creditCost === 1 ? '' : 's'} used
                        </Badge>
                      )}
                      {response.creditsRemaining !== null && (
                        <Badge className="rounded-full bg-stone-100 text-stone-700">
                          {response.creditsRemaining} remaining
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-5">
                <Tabs value={responseTab} onValueChange={(value) => setResponseTab(value as 'markdown' | 'json')} className="space-y-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <TabsList className="grid w-full grid-cols-2 rounded-full bg-stone-100/90 p-1 sm:w-[260px]">
                      <TabsTrigger value="markdown" className="rounded-full">Markdown</TabsTrigger>
                      <TabsTrigger value="json" className="rounded-full">Raw JSON</TabsTrigger>
                    </TabsList>
                    <p className="text-xs text-stone-500">
                      Text responses open in Markdown. Structured responses open as JSON.
                    </p>
                  </div>

                  <TabsContent value="markdown" className="mt-0 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-stone-900">Data Preview</div>
                      {markdownContent && (
                        <button type="button" onClick={() => copyText(markdownContent)} className="inline-flex items-center gap-1 text-xs font-medium text-stone-500 hover:text-stone-900">
                          <Copy className="h-3.5 w-3.5" />
                          Copy
                        </button>
                      )}
                    </div>
                    <MarkdownPreview content={markdownContent} />
                  </TabsContent>

                  <TabsContent value="json" className="mt-0 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-stone-900">Raw JSON</div>
                      {response && (
                        <button type="button" onClick={() => copyText(formatJson(response.body))} className="inline-flex items-center gap-1 text-xs font-medium text-stone-500 hover:text-stone-900">
                          <Copy className="h-3.5 w-3.5" />
                          Copy
                        </button>
                      )}
                    </div>
                    <pre className="max-h-[420px] min-h-[220px] overflow-auto rounded-2xl bg-[#1f2937] p-5 text-xs leading-6 text-slate-100">
                      {response ? formatJson(response.body) : 'Send a request to inspect the JSON response.'}
                    </pre>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-5">
            <Card className="overflow-hidden bg-white/80 shadow-sm backdrop-blur-xl">
              <CardHeader className="border-b border-stone-100 pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Activity className="h-5 w-5 text-stone-500" />
                  Usage Metering
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl bg-stone-100 p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">Estimated cost</div>
                    <div className="mt-2 text-2xl font-bold text-stone-950">
                      {selectedEndpoint.credits === 0 ? 'Free' : selectedEndpoint.credits}
                      {selectedEndpoint.credits > 0 && (
                        <span className="ml-1 text-sm font-medium text-stone-500">credits</span>
                      )}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-emerald-50 p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">Remaining</div>
                    <div className="mt-2 text-2xl font-bold text-emerald-950">
                      {displayedCreditsRemaining ?? '—'}
                    </div>
                  </div>
                </div>
                {creditMessage && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    {creditMessage}
                  </div>
                )}
                {freeCreditStatus?.alreadyClaimed && (
                  <div className="rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-600">
                    Claimed for {freeCreditStatus.period}. Next refill available {formatRefillDate(freeCreditStatus.nextRefillAt)}.
                  </div>
                )}
                <Button
                  variant="outline"
                  onClick={refreshFreeCredits}
                  disabled={refreshingCredits || freeCreditStatus?.alreadyClaimed}
                  className="w-full rounded-full border-stone-200 bg-white hover:bg-stone-50"
                >
                  {refreshingCredits && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {freeCreditStatus?.alreadyClaimed ? 'Available next month' : 'Refill free credits'}
                </Button>
              </CardContent>
            </Card>

            <Card className="overflow-hidden bg-white/80 shadow-sm backdrop-blur-xl">
              <CardHeader className="border-b border-stone-100 pb-4">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Code2 className="h-5 w-5 text-stone-500" />
                    Code Generator
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyText(codeSample)}
                    className="rounded-full border-stone-200 bg-white hover:bg-stone-50"
                  >
                    <Clipboard className="mr-2 h-4 w-4" />
                    Copy
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 p-4">
                <div className="grid grid-cols-3 gap-2 rounded-full bg-stone-100 p-1">
                  {(['curl', 'javascript', 'python'] as const).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setLanguage(item)}
                      className={cn(
                        'rounded-full px-3 py-2 text-xs font-semibold capitalize transition',
                        language === item ? 'bg-white text-stone-950 shadow-sm' : 'text-stone-500 hover:text-stone-800'
                      )}
                    >
                      {item === 'javascript' ? 'JS' : item}
                    </button>
                  ))}
                </div>
                <pre className="min-h-[260px] overflow-auto rounded-2xl bg-[#1f2937] p-4 text-xs leading-6 text-slate-100">
                  {codeSample}
                </pre>
              </CardContent>
            </Card>

            <Card className="overflow-hidden bg-white/80 shadow-sm backdrop-blur-xl">
              <CardHeader className="border-b border-stone-100 pb-4">
                <CardTitle className="text-lg">Request History</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 p-4">
                {requestHistory.length === 0 ? (
                  <p className="text-sm text-stone-500">Recent playground requests will appear here.</p>
                ) : (
                  requestHistory.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-stone-200/80 bg-[#fffdfa] p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-stone-900">{item.endpointTitle}</div>
                          <div className="mt-1 truncate font-mono text-xs text-stone-400">{item.method} {item.path}</div>
                        </div>
                        <Badge className={cn('rounded-full', item.status < 400 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>
                          {item.status}
                        </Badge>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-stone-500">
                        <span>{item.elapsedMs} ms</span>
                        {item.creditCost !== null && <span>{item.creditCost} credits</span>}
                        {item.creditsRemaining !== null && <span>{item.creditsRemaining} left</span>}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </div>
  )
}
